import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useLocalStorage } from 'usehooks-ts';

import { log } from '@/app/shared/utils/logger';
import { useRefreshMutation, useTelegramLoginMutation } from '@app/store/slices/auth/authApi';
import { ApiResponse } from '@/app/store/utils/common';
import { LOCAL_STORAGE } from '@app/shared/constants/common';
import {
  getCachedInitData,
  getInitDataFromUrl,
  getTelegramWebApp,
  hasInitDataSource,
  waitForTelegramInitData,
} from '@app/shared/utils/common';
import { useAppSelector } from '@app/store/hooks';
import { selectIsAuthenticated } from '@app/store/selectors';
import { setAccessToken, setAuthBootstrapping } from '@/app/store/slices/auth/authSlice';

interface AuthContextValue {
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

interface Props {
  children: ReactNode;
}

const INIT_DATA_WAIT_MS = 2500;
const INIT_DATA_RETRY_WAIT_MS = 5000;

export const AuthProvider = ({ children }: Props) => {
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const isBootstrapping = useRef(false);

  const giftFromUrl = searchParams.get('gift');
  const [, setGiftToken] = useLocalStorage<string | null>(LOCAL_STORAGE.gift, null);

  const [telegramLogin] = useTelegramLoginMutation();
  const [refresh] = useRefreshMutation();

  useEffect(() => {
    if (giftFromUrl) {
      setGiftToken(giftFromUrl);
    }
  }, [giftFromUrl, setGiftToken]);

  useEffect(() => {
    if (isAuthenticated) {
      isBootstrapping.current = false;
      dispatch(setAuthBootstrapping(false));
      setIsLoading(false);
      return;
    }

    if (isBootstrapping.current) {
      return;
    }
    isBootstrapping.current = true;
    dispatch(setAuthBootstrapping(true));

    const isInsideTelegram = hasInitDataSource();

    void log({
      level: 'info',
      message: 'authBootstrapStarted',
      extra: {
        isAuthenticated,
        isInsideTelegram,
        hasTelegramObject: typeof window.Telegram !== 'undefined',
        hasInitDataInUrl: !!getInitDataFromUrl(),
        hasCachedInitData: !!getCachedInitData(),
      },
    });

    const tryTelegramLogin = async (
      initData: string
    ): Promise<ApiResponse<{ accessToken?: string }>> => {
      const response = await telegramLogin({ initData }).unwrap();
      void log({
        level: 'info',
        message: 'authTelegramLoginResult',
        extra: {
          ok: response.ok,
          hasAccessToken: !!response.data?.accessToken,
        },
      });
      return response;
    };

    const tryRefresh = async (): Promise<ApiResponse<{ accessToken?: string }>> => {
      const response = await refresh().unwrap();
      void log({
        level: 'info',
        message: 'authRefreshResult',
        extra: {
          ok: response.ok,
          hasAccessToken: !!response.data?.accessToken,
        },
      });
      return response;
    };

    const bootstrap = async () => {
      let lastRes: ApiResponse<{ accessToken?: string }> = { ok: false, data: null };
      let lastError: unknown = null;
      let refreshAttempted = false;
      const attemptedInitData = new Set<string>();

      const hasAccessToken = () => !!lastRes.data?.accessToken;

      const attemptTelegramLogin = async (initData: string | null) => {
        if (!initData || attemptedInitData.has(initData)) {
          return;
        }
        attemptedInitData.add(initData);

        try {
          lastRes = await tryTelegramLogin(initData);
          lastError = null;
        } catch (error: unknown) {
          lastError = error;
        }
      };

      const attemptRefresh = async () => {
        if (refreshAttempted) {
          return;
        }
        refreshAttempted = true;

        try {
          lastRes = await tryRefresh();
          lastError = null;
        } catch (error: unknown) {
          lastError = error;
          lastRes = { ok: false, data: null };
        }
      };

      try {
        const resolved = isInsideTelegram ? await waitForTelegramInitData(INIT_DATA_WAIT_MS) : null;

        void log({
          level: 'info',
          message: 'authInitDataWaitResult',
          extra: {
            isInsideTelegram,
            hasInitData: !!resolved,
            initDataSource: resolved?.source ?? null,
            waitMs: INIT_DATA_WAIT_MS,
          },
        });

        if (resolved) {
          await attemptTelegramLogin(resolved.initData);
        } else {
          await attemptRefresh();
        }

        const hadFreshInitData = resolved?.source === 'sdk' || resolved?.source === 'url';
        if (!hasAccessToken() && isInsideTelegram && !hadFreshInitData) {
          const lateResolved = await waitForTelegramInitData(INIT_DATA_RETRY_WAIT_MS);
          void log({
            level: 'info',
            message: 'authLateInitDataWaitResult',
            extra: {
              hasInitData: !!lateResolved,
              initDataSource: lateResolved?.source ?? null,
              waitMs: INIT_DATA_RETRY_WAIT_MS,
            },
          });
          await attemptTelegramLogin(lateResolved?.initData ?? null);
        }

        if (!hasAccessToken() && isInsideTelegram) {
          const tg = getTelegramWebApp();
          const fallbackInitData =
            (tg?.initData && tg.initData.length > 0 ? tg.initData : null) ??
            getInitDataFromUrl() ??
            getCachedInitData();

          void log({
            level: 'info',
            message: 'authTelegramFallbackResult',
            extra: {
              hasTelegramObject: !!tg,
              hasInitData: !!fallbackInitData,
              fromUrl: !tg?.initData && !!getInitDataFromUrl(),
              fromCache: !tg?.initData && !getInitDataFromUrl() && !!getCachedInitData(),
              alreadyAttempted: !!fallbackInitData && attemptedInitData.has(fallbackInitData),
            },
          });

          await attemptTelegramLogin(fallbackInitData);
        }

        if (!hasAccessToken()) {
          await attemptRefresh();
        }

        if (lastRes.data?.accessToken) {
          dispatch(setAccessToken(lastRes.data.accessToken));
          void log({
            level: 'info',
            message: 'authBootstrapCompleted',
            extra: {
              isInsideTelegram,
              hasAccessToken: true,
            },
          });
        } else {
          void log({
            level: 'error',
            message: 'authError',
            error: lastError,
            extra: {
              isAuthenticated,
              isInsideTelegram,
              hadInitData: !!resolved,
              initDataSource: resolved?.source ?? null,
              initDataAttempts: attemptedInitData.size,
              refreshAttempted,
              res: JSON.stringify(lastRes),
            },
          });
        }
      } catch (error: unknown) {
        void log({
          level: 'error',
          message: 'authError (outer)',
          error,
          extra: { isAuthenticated, isInsideTelegram },
        });
      } finally {
        dispatch(setAuthBootstrapping(false));
        setIsLoading(false);
        isBootstrapping.current = false;
      }
    };

    void bootstrap();
  }, [dispatch, isAuthenticated, refresh, telegramLogin]);

  return <AuthContext.Provider value={{ isLoading }}>{children}</AuthContext.Provider>;
};
